import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';
import { WxDialog, WxLabel, WxPrice, WxText } from '../..';
import { VariantOrderItem } from '../../../hooks/api/queries/useVariantsByIds';
import { useCartContext } from '../../../hooks/context/useCartContext';
import { __ } from '../../../i18n/l10n';
import { breakpoint } from '../../../styles/constants';
import { IMAGE_RATIOS } from '../../../styles/imageRatios';
import { WImage } from '../../ui/image/Image';
import { RedLink } from '../order/OrderCard';

interface Props {
  item: VariantOrderItem;
  quantity: number;
  withActions?: boolean;
}

export const CartItem = ({ item, quantity, withActions = true }: Props) => {
  const navigate = useNavigate();
  const { incrementOne, removeItem, decrementOne } = useCartContext();
  const price = item.priceOverwrite ? item.priceOverwrite : item.price;
  const [deleteModal, setDeleteModal] = useState(false);

  return (
    <Wrapper>
      <WxDialog
        title={__('Confirm cancellation')}
        content={__('Are you sure you want to delete this item?')}
        open={deleteModal}
        setOpen={setDeleteModal}
        onClose={() => removeItem(item.variantId)}
      />
      <ImageDiv onClick={() => navigate(`/product/${item.slug}`)}>
        <WImage
          image={
            item.localizedVariantImage ??
            item.defaultVariantImage ??
            item.localizedProductImage ??
            item.defaultProductImage
          }
          background
        />
      </ImageDiv>
      <InfoDiv>
        <TopDiv>
          <WxLabel
            style={{ width: '100%' }}
            onClick={() => navigate(`/product/${item.slug}`)}
            label={!withActions && quantity < 2 ? item.label : `${item.label}`}
          />
          {withActions && (
            <div>
              <StyledFontAwesomeIcon
                icon={faMinus}
                onClick={() => {
                  if (quantity === 1) setDeleteModal(true);
                  else decrementOne(item.variantId);
                }}
              />
              {quantity}
              <StyledFontAwesomeIcon
                icon={faPlus}
                onClick={() => incrementOne(item.variantId)}
                disabled={item.availableQuantity < quantity + 1}
              />
            </div>
          )}
          {!withActions && (
            <WxLabel
              noUppercase
              label={`x${quantity}`}
              style={{ minWidth: 18 }}
            />
          )}
        </TopDiv>
        <MiddleDiv>
          {item.attributes
            ?.sort((a, b) => {
              if (a.keySorting === null) return 1;
              else if (b.keySorting === null) return -1;
              else return a.keySorting - b.keySorting;
            })
            .map((attr) => (
              <li key={`${item.variantId}-${attr.id}`}>
                <WxText text={attr.label} darkMode />
              </li>
            ))}
        </MiddleDiv>
        <BottomDiv withAction={withActions}>
          {withActions && (
            <RedLink onClick={() => setDeleteModal(true)}>
              {__('Delete')}
            </RedLink>
          )}

          <WxPrice
            price={price.milesAmount * quantity}
            priceBefore={
              item.priceOverwrite
                ? item.price?.milesAmount * quantity
                : undefined
            }
            dark
            background={false}
          />
        </BottomDiv>
      </InfoDiv>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  overflow: hidden;
  padding-bottom: 1.2rem;
  gap: 0.625rem;
`;

export const StyledFontAwesomeIcon = styled(FontAwesomeIcon)<{
  disabled?: boolean;
}>`
  font-weight: 900;
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      cursor: pointer;
      opacity: 0.3;
    `};
`;

const InfoDiv = styled.div`
  width: calc(66% - 0.3125rem);
  display: flex;
  flex-direction: column;
  padding-top: 0.625rem;
`;

const TopDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.9575rem;

  p {
    overflow: hidden;
  }

  > :last-child {
    display: flex;
    gap: 0.8125rem;
    margin-right: 0.5rem;
    color: ${(props) => props.theme.colors.white};
    font-size: 0.75rem;
  }
`;

const BottomDiv = styled.div<{ withAction: boolean }>`
  margin-top: auto;
  display: flex;
  justify-content: ${({ withAction }) =>
    withAction ? 'space-between' : 'flex-end'};
  align-items: center;
  @media (min-width: ${breakpoint}) {
    align-items: flex-end;
  }
`;

const MiddleDiv = styled.ul`
  text-align: left;
  margin-top: 0.625rem;

  > li {
  }
`;

const ImageDiv = styled.div`
  min-width: calc(41% - 0.3125rem);
  width: calc(41% - 0.3125rem);

  img {
    aspect-ratio: ${IMAGE_RATIOS.PRODUCT_LIST};
    object-fit: cover;
  }
`;
